/**
 * Image sizing for a reported Issue's screenshot — pure, so it is unit-tested.
 *
 * The marked-up image keeps the screenshot's natural size unless it would exceed the
 * upload cap; then it is re-encoded and, if still too big, downscaled step by step.
 * Delyver gets its own copy, no longer than 1,600 px on its long edge.
 */

export interface Size {
  width: number;
  height: number;
}

/** Scale `size` down (never up) so its long edge is at most `maxEdge`. */
export function fitWithin(size: Size, maxEdge: number): Size {
  const longEdge = Math.max(size.width, size.height);
  if (longEdge <= maxEdge) return { width: size.width, height: size.height };
  const ratio = maxEdge / longEdge;
  return {
    width: Math.max(1, Math.round(size.width * ratio)),
    height: Math.max(1, Math.round(size.height * ratio)),
  };
}

/**
 * The next, smaller size to try when an encoded image came out `bytes` long against a cap
 * of `maxBytes`. Encoded size scales roughly with pixel count, so shrink each edge by the
 * square root of the overshoot, with a 10% margin so the loop converges in a step or two.
 */
export function shrinkToward(size: Size, bytes: number, maxBytes: number): Size {
  const ratio = Math.min(0.9, Math.sqrt(maxBytes / Math.max(bytes, 1)) * 0.9);
  return {
    width: Math.max(1, Math.round(size.width * ratio)),
    height: Math.max(1, Math.round(size.height * ratio)),
  };
}

/** Image px per screen px, for a pointer at (clientX, clientY) over a displayed image. */
export function screenToImage(
  client: { x: number; y: number },
  displayed: { left: number; top: number; width: number; height: number },
  natural: Size,
): { x: number; y: number } {
  const sx = natural.width / Math.max(displayed.width, 1);
  const sy = natural.height / Math.max(displayed.height, 1);
  return { x: (client.x - displayed.left) * sx, y: (client.y - displayed.top) * sy };
}

export interface Placement extends Size {
  left: number;
  top: number;
}

/**
 * Where an image sits when fitted inside a box: as large as fits, never above its natural
 * size, centred. A capture of the current viewport (natural = viewport × pixel ratio)
 * therefore lands at exactly the viewport's size at (0, 0) — the page appears to freeze.
 */
export function fitContain(natural: Size, box: Size): Placement {
  const ratio = Math.min(
    1,
    box.width / Math.max(natural.width, 1),
    box.height / Math.max(natural.height, 1),
  );
  const width = Math.max(1, Math.round(natural.width * ratio));
  const height = Math.max(1, Math.round(natural.height * ratio));
  return {
    width,
    height,
    left: Math.round((box.width - width) / 2),
    top: Math.round((box.height - height) / 2),
  };
}
